import {
  Box,
  type BoxProps,
  Paper,
  Typography,
  mergeSlotProps,
} from '@mui/material';
import {type FC, useMemo} from 'react';
import useVisualizationParams from '@src/hooks/useVisualizationParams';

/**
 * Interpolates RGB color values based on the same logic used in SuperResImageLayer.
 * The interpolation matches the heatmap color scheme:
 * - Red: 1 at 0 → 0 at 0.5 → 0 at 1
 * - Green: 0 at 0 → 0 at 0.5 → 1 at 1
 * - Blue: 0 at 0 → 1 at 0.5 → 0 at 1
 *
 * @param normalizedValue - A value between 0 and 1 (confidence value normalized)
 * @returns RGB color as hex string
 */
const interpolateConfidenceColor = (normalizedValue: number): string => {
  const t = Math.max(0, Math.min(1, normalizedValue));

  let r: number;
  let g: number;
  let b: number;

  if (t <= 0.5) {
    // Interpolate from (1,0,0) [red] to (0,0,1) [blue] at t=0.5
    const tScaled = t * 2; // Scale to [0, 1] for this segment
    r = 1 - tScaled; // 1 → 0
    g = 0; // 0 → 0
    b = tScaled; // 0 → 1
  } else {
    // Interpolate from (0,0,1) [blue] to (0,1,0) [green] at t=1
    const tScaled = (t - 0.5) * 2; // Scale to [0, 1] for this segment
    r = 0; // 0 → 0
    g = tScaled; // 0 → 1
    b = 1 - tScaled; // 1 → 0
  }
  return `rgb( ${r * 255}, ${g * 255}, ${b * 255} )`;
};

type ConfidenceLegendProps = {rootProps?: BoxProps};

export const ConfidenceLegend: FC<ConfidenceLegendProps> = ({rootProps}) => {
  const isSuperRes = useVisualizationParams().isSuperRes;

  const steps = 10;
  const cells = useMemo(
    () =>
      Array.from({length: steps}, (_, i) => {
        const normalizedValue = i / (steps - 1); // 0 to 1
        const confidenceValue = Math.round((i / (steps - 1)) * 100); // 0 to 100

        return {
          position: normalizedValue,
          label: confidenceValue.toString(),
          color: interpolateConfidenceColor(normalizedValue),
        };
      }),
    [],
  );

  if (!isSuperRes) {
    return null;
  }

  return (
    <Box
      {...mergeSlotProps<BoxProps>(rootProps, {
        sx: {
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 2,
        },
      })}
    >
      <Box
        sx={{
          width: '100%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          marginBlockTop: 2,
        }}
      >
        <Box sx={{width: '100%', position: 'relative'}}>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
              height: 29,
              borderRadius: 0,
            }}
          >
            {cells.map(cell => (
              <Typography
                key={`tooltip${cell.position}`}
                variant="caption"
                sx={{
                  textAlign: 'center',
                  flex: 1,
                  color: 'text.secondary',
                  fontSize: '0.85rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {cell.label}
              </Typography>
            ))}
          </Paper>

          <Box sx={{position: 'relative'}}>
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                height: 22,
                backgroundImage: `
              linear-gradient(45deg, #ccc 25%, transparent 25%),
              linear-gradient(-45deg, #ccc 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #ccc 75%),
              linear-gradient(-45deg, transparent 75%, #ccc 75%)
            `,
                backgroundSize: '8px 8px',
                backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
              }}
            />

            <Box
              sx={{
                display: 'flex',
                height: 22,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {cells.map(cell => (
                <Box
                  key={`cell-${cell.position}`}
                  sx={{
                    flex: 1,
                    position: 'relative',
                    backgroundColor: cell.color,
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
